import { notify } from 'notiwind';

/**
 * Normalizes error responses for consistent handling
 * @param {Object} error - The error object from catch block
 * @returns {Object} Normalized error object
 */
export function normalizeError(error) {
    // If it's an axios error response
    if (error.response) {
        return {
            status: error.response.status,
            data: error.response.data,
            message: error.response.data?.message || error.message
        };
    }
    
    // If it's already a response object
    if (error.status) {
        return error;
    }
    
    // For other types of errors
    return {
        status: 500,
        message: error.message || 'An unexpected error occurred',
        data: { errors: { general: [error.message || 'An unexpected error occurred'] } }
    };
}

/**
 * Global error handler that displays appropriate error messages using notiwind
 * Handles server errors, Laravel validation errors, and custom errors
 * @param {Object} error - The error object from catch block or response
 * @param {Object} options - Optional configuration
 * @param {string} options.defaultTitle - Default title for error notifications
 * @param {boolean} options.showValidationErrors - Whether to show individual validation errors
 */
export function handleHttpError(error, options = {}) {
    if (!error) return;

    const {
        defaultTitle = 'Error',
        showValidationErrors = true
    } = options;

    const response = normalizeError(error);
    if (!response) return;

    // Handle authentication errors (401/403) - redirect to login
    if (response.status && (response.status === 401 || response.status === 403)) {
        location.href = '/login?loginstatus=autologout';
        return;
    }

    // Handle Laravel validation errors (422)
    if (response.status === 422 && response.data && response.data.errors && showValidationErrors) {
        // Show each validation error as a separate notification
        for (const field in response.data.errors) {
            const fieldErrors = response.data.errors[field];
            if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach(errorMessage => {
                    notify({
                        group: 'error',
                        title: 'Validation Error',
                        text: errorMessage,
                    }, 5000);
                });
            }
        }
        return;
    }

    // Handle general server errors with specific messages
    if (response.message && response.message.length > 3) {
        let errorTitle = defaultTitle;
        let errorMessage = response.message;

        // Handle specific HTTP status codes
        if (response.status) {
            switch (response.status) {
                case 404:
                    errorTitle = 'Not Found';
                    errorMessage = 'The requested resource was not found.';
                    break;
                case 500:
                    errorTitle = 'Server Error';
                    errorMessage = 'Sorry, there is a problem trying to complete your request. Please try again later.';
                    break;
                case 429:
                    errorTitle = 'Too Many Requests';
                    errorMessage = 'You are making too many requests. Please wait a moment and try again.';
                    break;
                case 503:
                    errorTitle = 'Service Unavailable';
                    errorMessage = 'The service is temporarily unavailable. Please try again later.';
                    break;
                default:
                    // Use the server message for other status codes
                    if (response.message.includes('404') || response.message.includes('500')) {
                        errorMessage = 'Sorry, there is a problem trying to complete your request. ' + response.message;
                    }
                    break;
            }
        }

        notify({
            group: 'error',
            title: errorTitle,
            text: errorMessage,
        }, 5000);
        return;
    }

    // Handle Laravel form request errors or other structured errors
    if (response.data && response.data.errors) {
        for (const key in response.data.errors) {
            const errors = response.data.errors[key];
            if (Array.isArray(errors)) {
                errors.forEach(errorMessage => {
                    notify({
                        group: 'error',
                        title: defaultTitle,
                        text: errorMessage,
                    }, 5000);
                });
            }
        }
        return;
    }

    // Fallback for any other error types
    notify({
        group: 'error',
        title: defaultTitle,
        text: 'An unexpected error occurred. Please try again.',
    }, 5000);
}

/**
 * Main error handling function that replaces this.$root.handleHttpStatus
 * This is the primary function that should be used throughout the application
 * @param {Object} error - The error object or response
 * @param {Object} options - Optional configuration
 */
export function handleError(error, options = {}) {
    return handleHttpError(error, options);
}
